import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Sparkles,
  Send,
  Music2,
  ListMusic,
  Users,
  Megaphone,
  Newspaper,
  Bot,
  Wand2,
  Clapperboard,
  Target,
  ShieldCheck,
  Timer,
  BarChart3,
  Flame,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import promoteHero from "@/assets/promote-hero.jpg";
import promoteCreators from "@/assets/promote-creators.jpg";
import promoteSound from "@/assets/promote-sound.jpg";
import { Button } from "@/components/ui/button";
import { WaitlistForm } from "@/components/waitlist-form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PROMOTE_OG_IMAGE, PROMOTE_OG_IMAGE_ALT, SITE_NAME, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/promote")({
  head: () =>
    buildPageMetadata({
      path: "/promote",
      title: "Promote — Promotion Marketplace Coming Soon | BeHot.Now",
      description:
        "Discover the BeHot.Now promotion marketplace coming soon: creators, music promotion, influencer campaigns, UGC, startup promotion and more. Join the waitlist.",
      keywords: [
        "promote your music",
        "playlist placement",
        "music promotion",
        "brand promotion",
        "social media ads",
        "meta ads for creators",
        "product launch promotion",
        "influencer promotion",
        "promote your startup",
      ],
      ogTitle: "Promote — every campaign type in one place",
      ogDescription:
        "Directory submissions, playlist curator pitching, influencer and UGC collabs, press and AI-built ad campaigns. Coming soon.",
      image: PROMOTE_OG_IMAGE,
      imageAlt: PROMOTE_OG_IMAGE_ALT,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Promote", path: "/promote" },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "BeHot.Now promotion marketplace",
        serviceType:
          "Music promotion, influencer campaigns, UGC, startup and AI product promotion",
        url: `${SITE_URL}/promote`,
        provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
      },
    }),
  component: Promote,
});

const SERVICE_ICONS = [Send, Music2, ListMusic, Users, Megaphone, Newspaper, Bot, Target, Wand2, Clapperboard];
const SERVICE_POINT_COUNTS = [4, 4, 6, 4, 4, 4, 4, 5, 5, 5];
const AI_STEP_COUNT = 4;
const FLOW_COUNT = 4;
const GUARANTEE_ICONS = [ShieldCheck, Timer, BarChart3];
const FAQ_KEYS = ["a", "b", "c", "d", "e", "f", "g"];

function Promote() {
  const { t } = useT();

  const services = SERVICE_ICONS.map((icon, i) => ({
    icon,
    title: t(`marketing.promote.svc.${i}.title`),
    body: t(`marketing.promote.svc.${i}.body`),
    points: Array.from({ length: SERVICE_POINT_COUNTS[i] ?? 0 }, (_, j): string =>
      t(`marketing.promote.svc.${i}.p.${j}`)
    ),
  }));

  const aiSteps = Array.from({ length: AI_STEP_COUNT }, (_, i) => ({
    title: t(`marketing.promote.ai.${i}.title`),
    body: t(`marketing.promote.ai.${i}.body`),
  }));

  const flow = Array.from({ length: FLOW_COUNT }, (_, i) => ({
    title: t(`marketing.promote.flow.${i}.title`),
    body: t(`marketing.promote.flow.${i}.body`),
  }));

  const guarantees = GUARANTEE_ICONS.map((icon, i) => ({
    icon,
    title: t(`marketing.promote.guar.${i}.title`),
    body: t(`marketing.promote.guar.${i}.body`),
  }));

  return (
    <div className="mx-auto max-w-5xl px-4 py-12">
      <section className="card-surface overflow-hidden p-0">
        <div className="grid items-center gap-6 md:grid-cols-[1.1fr_1fr]">
          <div className="p-8 sm:p-10">
            <Badge variant="secondary" className="uppercase tracking-widest">{t("marketing.promote.badge_soon")}</Badge>
            <h1 className="mt-4 font-display text-4xl font-bold sm:text-5xl">
              {t("marketing.promote.h1_pre")} <span className="text-gradient-hot">{t("marketing.promote.h1_grad")}</span>
            </h1>
            <p className="mt-3 text-muted-foreground">
              {t("marketing.promote.subtitle")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild variant="hot">
                <Link to="/get-hot" search={{ listing: undefined, category: undefined }}>
                  <Flame className="size-4" /> {t("marketing.promote.cta_board")}
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/how-it-works">{t("marketing.promote.cta_how")}</Link>
              </Button>
            </div>
          </div>
          <img
            src={promoteHero}
            alt={t("marketing.promote.hero_alt")}
            width={1600}
            height={900}
            className="h-full w-full object-cover"
          />
        </div>
      </section>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {[
          { k: "9", v: t("marketing.promote.stat1_v") },
          { k: "1", v: t("marketing.promote.stat2_v") },
          { k: "100%", v: t("marketing.promote.stat3_v") },
        ].map((x) => (
          <div key={x.v} className="card-surface p-5">
            <p className="font-display text-3xl font-bold text-gradient-hot">{x.k}</p>
            <p className="text-sm text-muted-foreground">{x.v}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 grid gap-4 md:grid-cols-2">
        <div className="card-surface overflow-hidden">
          <img
            src={promoteCreators}
            alt={t("marketing.promote.creators_alt")}
            loading="lazy"
            width={1200}
            height={800}
            className="h-48 w-full object-cover"
          />
          <div className="p-6">
            <h3 className="font-display text-lg font-bold">{t("marketing.promote.creators_title")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("marketing.promote.creators_body")}
            </p>
          </div>
        </div>
        <div className="card-surface overflow-hidden">
          <img
            src={promoteSound}
            alt={t("marketing.promote.sound_alt")}
            loading="lazy"
            width={1200}
            height={800}
            className="h-48 w-full object-cover"
          />
          <div className="p-6">
            <h3 className="font-display text-lg font-bold">{t("marketing.promote.sound_title")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("marketing.promote.sound_body")}
            </p>
          </div>
        </div>
      </div>

      <h2 className="mt-14 font-display text-2xl font-bold">{t("marketing.promote.services_heading")}</h2>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {services.map((s) => (
          <div key={s.title} className="card-surface p-6">
            <div className="flex items-center justify-between gap-3">
              <s.icon className="size-6 text-creative" />
              <Badge variant="outline" className="text-[11px]">{t("marketing.promote.badge_soon")}</Badge>
            </div>
            <h3 className="mt-3 font-display text-lg font-bold">{s.title}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
            <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
              {s.points.map((p) => (
                <li key={p} className="flex gap-2">
                  <Sparkles className="mt-0.5 size-3.5 shrink-0 text-primary" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="card-surface mt-14 p-8">
        <div className="flex flex-wrap items-center gap-3">
          <Wand2 className="size-6 text-creative" />
          <h2 className="font-display text-2xl font-bold">
            {t("marketing.promote.ai_heading_pre")} <span className="text-gradient-hot">{t("marketing.promote.ai_heading_grad")}</span>
          </h2>
          <Badge variant="outline" className="text-[11px]">{t("marketing.promote.badge_soon")}</Badge>
        </div>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          {t("marketing.promote.ai_intro")}
        </p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {aiSteps.map((s, i) => (
            <div key={s.title} className="rounded-xl border border-border bg-background p-5">
              <div className="rank-numeral text-2xl leading-none">{String(i + 1).padStart(2, "0")}</div>
              <p className="mt-2 font-display font-semibold">{s.title}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          {t("marketing.promote.ai_footnote")}
        </p>
      </div>

      <h2 className="mt-14 font-display text-2xl font-bold">{t("marketing.promote.flow_heading")}</h2>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {flow.map((f, i) => (
          <div key={f.title} className="card-surface p-5">
            <div className="rank-numeral text-2xl leading-none">{String(i + 1).padStart(2, "0")}</div>
            <p className="mt-2 font-display font-semibold">{f.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">{f.body}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 grid gap-4 sm:grid-cols-3">
        {guarantees.map((g) => (
          <div key={g.title} className="card-surface p-6">
            <g.icon className="size-6 text-success" />
            <p className="mt-3 font-display font-bold">{g.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">{g.body}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-14 font-display text-2xl font-bold">{t("marketing.promote.pricing_heading")}</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        {t("marketing.promote.pricing_body")}
      </p>

      <h2 className="mt-14 font-display text-2xl font-bold">{t("marketing.promote.questions_heading")}</h2>
      <Accordion type="single" collapsible className="mt-4">
        {FAQ_KEYS.map((k) => (
          <AccordionItem value={k} key={k}>
            <AccordionTrigger>{t(`marketing.promote.faq.${k}.q`)}</AccordionTrigger>
            <AccordionContent>
              {t(`marketing.promote.faq.${k}.a`)}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="card-surface mt-14 p-8 text-center">
        <Sparkles className="mx-auto size-8 text-creative" />
        <h2 className="mt-3 font-display text-2xl font-bold">{t("marketing.promote.final_heading")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("marketing.promote.final_body")}
        </p>
        <WaitlistForm product="promote" cta={t("marketing.promote.waitlist_cta")} />
      </div>
    </div>
  );
}
